import { useCallback, useImperativeHandle, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Flex, Text } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { Colors, Sizes } from "@jsq/ds/styles";
import * as styles from "./styles_vanilla.css";
import { TaxResidencyForm } from "./TaxResidencyForm";
import { TaxResidencyTable } from "./TaxResidencyTable";
import type { SectionActionHandle, TaxFormValues, TaxResidencyEntry } from "../../types";

export type TaxResidencySectionHandle = SectionActionHandle;

type TaxResidencySectionProps = {
  fieldName: string;
  required?: boolean;
  readonly?: boolean;
  onAddRef?: React.RefObject<SectionActionHandle | null>;
};

const getEntries = (values: TaxFormValues, fieldName: string): TaxResidencyEntry[] =>
  (values[fieldName] as TaxResidencyEntry[]) || [];

export const TaxResidencySection = ({
  fieldName,
  required = true,
  readonly = false,
  onAddRef,
}: TaxResidencySectionProps) => {
  const { values, setFieldValue, errors } = useFormContext<TaxFormValues>();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const originalEntryRef = useRef<TaxResidencyEntry | null>(null);

  const entries = getEntries(values, fieldName);
  const confirmedEntries = entries.filter((e) => !e._isDraft);
  const fieldError = errors[fieldName];
  const isFormVisible = editingIndex !== null;

  const addEntry = useCallback(() => {
    if (readonly || isFormVisible) return;
    const draftEntry: TaxResidencyEntry = {
      id: uuidv4(),
      jurisdiction: "",
      hasTaxReferenceNumber: true,
      taxReferenceNumberType: "",
      taxReferenceNumber: "",
      reasonForNoTin: "",
      _isDraft: true,
    };
    const newEntries = [...getEntries(values, fieldName), draftEntry];
    originalEntryRef.current = null;
    void setFieldValue(fieldName, newEntries);
    setEditingIndex(newEntries.length - 1);
  }, [fieldName, isFormVisible, readonly, setFieldValue, values]);

  useImperativeHandle(onAddRef, () => ({ addEntry }), [addEntry]);

  const handleEditClick = useCallback(
    (entry: TaxResidencyEntry) => {
      const currentEntries = getEntries(values, fieldName);
      const index = currentEntries.findIndex((e) => e.id === entry.id);
      if (index !== -1) {
        originalEntryRef.current = { ...currentEntries[index] };
        setEditingIndex(index);
      }
    },
    [fieldName, values],
  );

  const handleDeleteClick = useCallback(
    (entryId: string) => {
      const updatedEntries = getEntries(values, fieldName).filter((e) => e.id !== entryId);
      void setFieldValue(fieldName, updatedEntries);
    },
    [fieldName, setFieldValue, values],
  );

  const handleFormSubmit = useCallback(() => {
    if (editingIndex === null) return;
    const updatedEntries = getEntries(values, fieldName).map((e, i) =>
      i === editingIndex ? { ...e, _isDraft: undefined } : e,
    );
    void setFieldValue(fieldName, updatedEntries);
    originalEntryRef.current = null;
    setEditingIndex(null);
  }, [editingIndex, fieldName, setFieldValue, values]);

  const handleFormCancel = useCallback(() => {
    if (editingIndex === null) return;
    const currentEntries = getEntries(values, fieldName);
    const currentEntry = currentEntries[editingIndex];

    if (currentEntry?._isDraft) {
      void setFieldValue(
        fieldName,
        currentEntries.filter((_, i) => i !== editingIndex),
      );
    } else if (originalEntryRef.current) {
      void setFieldValue(
        fieldName,
        currentEntries.map((e, i) => (i === editingIndex ? originalEntryRef.current! : e)),
      );
    }
    originalEntryRef.current = null;
    setEditingIndex(null);
  }, [editingIndex, fieldName, setFieldValue, values]);

  return (
    <Flex
      flexDirection="column"
      gap={Sizes.MD}
      className={styles.container}
      data-testid={`section-${fieldName}`}
    >
      {confirmedEntries.length > 0 && !isFormVisible && (
        <>
          <TaxResidencyTable
            entries={confirmedEntries}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            readonly={readonly}
          />
          {required && (
            <Text className={styles.requiredText}>
              This is required <span className={styles.requiredAsterisk}>*</span>
            </Text>
          )}
        </>
      )}
      {confirmedEntries.length === 0 && !isFormVisible && (
        <>
          <Text className={styles.emptyStateText}>
            There are no tax residencies.{" "}
            {required && <span className={styles.requiredAsterisk}>*</span>}
          </Text>
          {typeof fieldError === "string" && (
            <Flex alignItems="center" gap="4px" className={styles.errorMessageContainer}>
              <Icon type="exclamation-circle" color={Colors.Danger600} size="small" />
              <Text className={styles.errorText}>{fieldError}</Text>
            </Flex>
          )}
        </>
      )}
      {isFormVisible && editingIndex !== null && (
        <TaxResidencyForm
          fieldName={fieldName}
          index={editingIndex}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          isNewEntry={entries[editingIndex]?._isDraft === true}
        />
      )}
    </Flex>
  );
};

export { TaxResidencyForm } from "./TaxResidencyForm";
export { TaxResidencyTable } from "./TaxResidencyTable";
